import React from 'react';

import { MenuItem, Select } from '@mui/material';
import { GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid';

import type { definitions } from '@luxuryescapes/contract-svc-promo';

import { VOUCHER_CONDITION_TYPES } from '~/components/Vouchers/voucherConditionEditConstants';
import {
  formatConditionTypeOptionLabel,
  getConditionTypeCategory,
  sortConditionTypeOptions,
} from '~/components/Vouchers/voucherConditionFormatters';

type ConditionTypeEditCellProps = GridRenderEditCellParams & {
  conditionTypes?: ReadonlyArray<definitions['VoucherConditionV1']['condition_type']>;
  deprecatedConditionTypes?: ReadonlySet<definitions['VoucherConditionV1']['condition_type']>;
  /** Fires when the user picks a new condition type while the cell is in edit mode (before row save). */
  onConditionTypeChange?: (nextType: string) => void;
};

export default function ConditionTypeEditCell({
  id,
  field,
  value,
  conditionTypes,
  deprecatedConditionTypes,
  onConditionTypeChange,
}: ConditionTypeEditCellProps) {
  const apiRef = useGridApiContext();
  const types = conditionTypes && conditionTypes.length > 0 ? conditionTypes : VOUCHER_CONDITION_TYPES;
  const options = types
    .map((type) => ({
      type,
      category: getConditionTypeCategory(type),
      label: formatConditionTypeOptionLabel(type),
      isBeingDeprecated: deprecatedConditionTypes?.has(type) ?? false,
    }))
    .sort(sortConditionTypeOptions);
  return (
    <Select
      value={value || ''}
      onChange={(event) => {
        const newValue = event.target.value;
        apiRef.current.setEditCellValue({ id, field, value: newValue });
        apiRef.current.setEditCellValue({ id, field: 'condition_details', value: newValue });
        onConditionTypeChange?.(newValue);
      }}
      size="small"
      fullWidth
    >
      {options.map(({ type, isBeingDeprecated }) => (
        <MenuItem key={type} value={type} sx={{ textDecoration: isBeingDeprecated ? 'line-through' : 'none' }}>
          {formatConditionTypeOptionLabel(type)}
        </MenuItem>
      ))}
    </Select>
  );
}
